import { consume } from '@lit/context';
import { LitElement, css, html, nothing, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { styleMap } from 'lit/directives/style-map.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import {
  ANNOUNCEMENT_LONG_FADE_OUT_MS,
  announcementFadeTiming,
  announcementStyles,
} from '../../../theme/announcement.css';
import { NOTICE_COLUMN_MAX_PX } from '../../../theme/notice.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type UIState } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { noticesStack } from '../layout';

/** How long a level-up stays up, its fade included. Legacy showed it for 2s
 *  before the fade; this one is lengthened by choice. */
const DISMISS_MS = 8000;

/** The same for every notice, so built once rather than per render. */
const FADE_TIMING = announcementFadeTiming(DISMISS_MS, ANNOUNCEMENT_LONG_FADE_OUT_MS);

/**
 * Level Up Notice - the "NEW LEVEL REACHED" panel
 *
 * - Level badge with a gentle pulse, the new level over it
 * - Sits above the plane, scaled down on shorter screens to stay clear of it
 * - Waits while a match result shows, since a win's bounty can level you up,
 *   and in the notice column (a phone, a short window) behind a death notice
 * - Shows for 8 seconds, ending on legacy's long fade. Click-through, like the
 *   other notices: Escape, a tap on the game or opening a panel clears it
 *   sooner (closeAllPanels).
 *
 * @element level-up-notice
 */
@customElement('level-up-notice')
export class LevelUpNotice extends LitElement {
  static override styles = [
    tokens,
    announcementStyles,
    css`
      /* Legacy centred it at 22.5% of the height, above the plane at the
         centre (the results sit at 50%). The max() keeps its top under the
         top HUD, which ends at 82px (game-mode-status's CTF flags), and
         --fit, the panel's scale, shrinks it on shorter screens so its bottom
         stays above the plane. 150px is half the panel's unscaled height. */
      :host {
        --fit: 1;
      }

      @media (max-height: 869px) {
        :host {
          --fit: 0.8;
        }
      }

      @media (max-height: 749px) {
        :host {
          --fit: 0.62;
        }
      }

      .result {
        top: max(22.5%, 90px + env(safe-area-inset-top, 0px) + 150px * var(--fit));
      }

      /* The scale goes here rather than on .result, whose transform is its
         centring and pop-in: a scale beside it would also scale the
         centring offset and pull the panel off-centre. */
      .panel {
        position: relative;
        width: min(400px, calc(100vw - 16px));
        height: 300px;
        scale: var(--fit);
        background-color: var(--color-bg-hover);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        box-sizing: border-box;
      }

      .leveltext {
        color: var(--color-text-heading);
        text-align: center;
        font-weight: bold;
        font-size: var(--font-size-xxl);
        height: 40px;
        margin-top: 24px;
      }

      .levelbadge {
        margin: 12px auto 26px auto;
        width: 192px;
        height: 192px;
        background-repeat: no-repeat;
        background-size: 192px 192px;
        background-image: url('/vanilla/ui/gameplay/level-badge.svg');
        animation: bountyshake 3s ease-in-out infinite;
        opacity: 0.9;
      }

      .levelnum {
        color: var(--color-text-white);
        position: absolute;
        text-align: center;
        font-weight: bold;
        width: 100%;
        top: 118px;
        font-size: 66px;
        text-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
      }

      /* In airmash-game's notice column (.notice-lanes) on a compact viewport:
         a block of the column rather than a band above the plane. The panel
         keeps its --fit scale, from its top edge now, and gives back the
         height the scale frees, so the notices under it close up; on a short
         screen it is half size, so a level-up and a kill fit the column
         together. The pop and fade lose the centring translate. */
      @media (max-width: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px), (max-height: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px) {
        :host {
          position: relative;
          height: auto;
        }

        .result {
          position: relative;
          top: auto;
          left: auto;
          width: fit-content;
          margin: 0 auto 8px;
          transform: none;
          animation-name: resultIn-lane, resultOut-lane;
        }

        .panel {
          transform-origin: 50% 0;
          margin-bottom: calc(-300px * (1 - var(--fit)));
        }
      }

      @media (max-height: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px) {
        :host {
          --fit: 0.5;
        }
      }

      @keyframes resultIn-lane {
        from {
          opacity: 0;
          transform: scale(0.9);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      @keyframes resultOut-lane {
        to {
          opacity: 0;
          transform: scale(0.9);
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  private dismissTimer: number | null = null;
  private lastShown: UIState['levelUp'] = null;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.clearDismissTimer();
  }

  private clearDismissTimer(): void {
    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }

  /** The level-up on screen. None while a match result is up: ab-server sends
   *  a win and the level-up from its bounty in the same tick, so it follows the
   *  result rather than covering it, and gets its full time after. None while
   *  a death notice is up in the notice column either (a phone, a short
   *  window): the column stacks the two, and on a landscape phone the level-up
   *  pushed the death notice onto ENTER SPECTATOR MODE, and with a kill as
   *  well off the bottom; it comes back once the death notice has gone. */
  private shownNotice(): UIState['levelUp'] {
    const { levelUp, btrMatchResult, ctfMatchResult, deathNotice } = this.uiState;
    if (btrMatchResult || ctfMatchResult) return null;
    if (deathNotice && noticesStack(this.uiState.viewport)) return null;
    return levelUp;
  }

  override updated() {
    const notice = this.shownNotice();

    if (notice === this.lastShown) return;
    this.lastShown = notice;

    this.clearDismissTimer();

    if (notice) {
      // Capture this notice so a timer firing just as a NEW level-up lands
      // can't dismiss the newer one. Games.ts replaces levelUp with a fresh
      // object per level, so the identity comparison is reliable.
      this.dismissTimer = window.setTimeout(() => {
        this.dismissTimer = null;
        if (this.uiState.levelUp === notice) this.gameApi.dismissLevelUpNotice();
      }, DISMISS_MS);
    }
  }

  override render() {
    const notice = this.shownNotice();

    if (!notice) {
      return nothing;
    }

    // Keyed per notice, so a new level-up while one shows gets a fresh
    // element: it pops in again, and its fade restarts with its timer.
    return keyed(
      notice,
      html`
        <div class="result" style=${styleMap(FADE_TIMING)}>
          <div class="panel">
            <div class="leveltext">NEW LEVEL REACHED</div>
            <div class="levelbadge"></div>
            <div class="levelnum">${notice.level}</div>
          </div>
        </div>
      `,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'level-up-notice': LevelUpNotice;
  }
}
